import {
  BitkitDefinitionTooltip,
  BitkitIconComponent,
  IconArrowRight,
  IconBranch,
  IconTag,
  IconWarningYellow,
  rem,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Card, type CardRootProps } from '@chakra-ui/react/card';
import { Text } from '@chakra-ui/react/text';
import { ReactNode, useEffect } from 'react';

import datadogRumCustomTiming from '@/utils/datadogRumCustomTiming';
import { pluralizeWordWithCount } from '@/utils/pluralizedWordWithCount';

import ColorBar, { ColorBarProps } from '../ColorBar/ColorBar';

export interface RunnableSummaryCardProps extends CardRootProps {
  abortReason?: string;
  bottlenecksCount?: number;
  bottlenecksUrl?: string;
  commitMessage?: string;
  datadogName?: string;
  datadogPage?: string;
  footerLeftSlot?: ReactNode;
  footerRightSlot?: ReactNode;
  headerRightSlot?: ReactNode;
  pullRequestOrCommitHashLink?: ReactNode;
  runnableName: string;
  sourceBranch?: string;
  statusColor: ColorBarProps['color'];
  statusTag: ReactNode;
  tag?: string;
  targetBranch?: string;
  triggeredBy?: string;
  triggerTypeIcon: BitkitIconComponent;
}

const RunnableSummaryCard = (props: RunnableSummaryCardProps) => {
  const {
    abortReason,
    bottlenecksCount,
    bottlenecksUrl,
    commitMessage,
    datadogName,
    datadogPage,
    footerLeftSlot,
    footerRightSlot,
    headerRightSlot,
    pullRequestOrCommitHashLink,
    runnableName,
    sourceBranch,
    statusTag,
    statusColor,
    tag,
    targetBranch,
    triggeredBy,
    triggerTypeIcon: TriggerTypeIcon,
    ...rest
  } = props;

  useEffect(() => {
    if (datadogName && datadogPage) {
      datadogRumCustomTiming(datadogPage, datadogName);
    }
  }, [datadogName, datadogPage]);

  return (
    <Card.Root display="flex" overflow="hidden" {...rest}>
      <ColorBar color={statusColor} width="4" />
      <Box width="100%">
        <Box as="header" display="flex" alignItems="center" flexWrap="wrap" padding="8" gap="8">
          {statusTag}
          {bottlenecksCount ? (
            <Box alignItems="center" display="flex">
              <BitkitDefinitionTooltip
                button={bottlenecksUrl ? { children: 'View in Insights', href: bottlenecksUrl } : undefined}
                text={`${bottlenecksCount} ${pluralizeWordWithCount('bottleneck', bottlenecksCount)} detected.`}
                textSize="md"
                truncate={false}
              >
                {runnableName}
              </BitkitDefinitionTooltip>
              <IconWarningYellow size="16" marginInlineStart="4" />
            </Box>
          ) : (
            <Text as="span" textStyle="body/md/semibold">
              {runnableName}
            </Text>
          )}
          {!!triggeredBy && (
            <Text
              as="span"
              textStyle="body/md/regular"
              color="text/secondary"
              _before={{
                content: `'•'`,
                marginInlineEnd: '6',
              }}
            >
              {triggeredBy}
            </Text>
          )}
          <Box marginInlineStart="auto">{headerRightSlot}</Box>
        </Box>
        <Box
          paddingBlock="8"
          paddingInline="12"
          borderBlock="1px solid"
          borderColor="border/minimal"
          display="flex"
          flexDirection="column"
          gap="4"
        >
          <Box display="flex" alignItems="center" gap="4">
            <TriggerTypeIcon size="16" color="icon/secondary" />
            {commitMessage?.split('\n')[0] || 'No commit message'}
            {pullRequestOrCommitHashLink}
          </Box>
          <Box display="flex" gap="4" alignItems="center" minWidth={0}>
            {tag ? (
              <>
                <IconTag size="16" color="icon/secondary" />
                <Text as="span" color="text/secondary" textStyle="body/md/regular" truncate>
                  {tag}
                </Text>
              </>
            ) : (
              <>
                <IconBranch size="16" color="icon/secondary" />
                {sourceBranch && (
                  <>
                    <Text as="span" color="text/secondary" textStyle="body/md/regular" truncate>
                      {sourceBranch}
                    </Text>
                    {!!targetBranch && <IconArrowRight size="16" color="icon/secondary" />}
                  </>
                )}
                {targetBranch && (
                  <Text as="span" color="text/secondary" textStyle="body/md/regular" truncate>
                    {targetBranch}
                  </Text>
                )}
              </>
            )}
          </Box>
          {!!abortReason && (
            <Text backgroundColor="status/warning/bg" textStyle="body/md/regular" width="fit-content">
              {abortReason}
            </Text>
          )}
        </Box>
        <Box
          as="footer"
          paddingInline="8"
          paddingBlock={rem(10)}
          display="flex"
          flexDirection={['column', 'row']}
          justifyContent="space-between"
          gap="8"
        >
          <Box display="flex" flexWrap="wrap">
            {footerLeftSlot}
          </Box>
          <Box display="flex" flexWrap="wrap">
            {footerRightSlot}
          </Box>
        </Box>
      </Box>
    </Card.Root>
  );
};

export default RunnableSummaryCard;
